import { useEffect, useState, useCallback, useMemo } from "react";

/**
 * ScreenProtection
 * - Blacks out the screen when:
 *   • PrintScreen / Snipping shortcuts pressed (Win+Shift+S, Cmd+Shift+3/4/5, etc.)
 *   • Tab/window loses focus (alt-tab, screen recording sometimes triggers this)
 *   • Document becomes hidden
 *   • DevTools open (heuristic: window size delta)
 * - Disables right-click & text selection across the wrapped area
 * - Adds a subtle live watermark with username + timestamp
 *
 * Note: web browsers cannot 100% prevent OS-level screen capture, but this
 * raises the bar significantly and discourages casual capture.
 */
export default function ScreenProtection({ username = "client", children }) {
  const [hidden, setHidden] = useState(false);
  const [reason, setReason] = useState("");

  const blackOut = useCallback((msg) => {
    setReason(msg);
    setHidden(true);
    setTimeout(() => setHidden(false), 2500);
  }, []);

  const tryClearClipboard = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText("");
      }
    } catch (err) {
      console.warn("clipboard clear blocked:", err?.message || err);
    }
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKeyDown = (e) => {
      const k = (e.key || "").toLowerCase();
      // PrintScreen
      if (k === "printscreen" || e.code === "PrintScreen") {
        tryClearClipboard();
        blackOut("Captura bloqueada");
        return;
      }
      // Windows snipping: Win+Shift+S
      if (e.shiftKey && (e.metaKey || e.getModifierState?.("OS")) && k === "s") {
        blackOut("Captura bloqueada");
        return;
      }
      // macOS screenshots: Cmd+Shift+3 / 4 / 5
      if (e.metaKey && e.shiftKey && ["3", "4", "5"].includes(k)) {
        blackOut("Captura bloqueada");
        return;
      }
      // DevTools shortcuts
      if (
        k === "f12" ||
        (e.ctrlKey && e.shiftKey && ["i", "j", "c"].includes(k)) ||
        (e.metaKey && e.altKey && ["i", "j", "c"].includes(k))
      ) {
        e.preventDefault();
        blackOut("Inspección no permitida");
      }
      // Save page
      if ((e.ctrlKey || e.metaKey) && k === "s") {
        e.preventDefault();
        blackOut("Acción bloqueada");
      }
    };

    const onKeyUp = (e) => {
      if ((e.key || "").toLowerCase() === "printscreen") {
        tryClearClipboard();
        blackOut("Captura bloqueada");
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp, true);
    };
  }, [blackOut, tryClearClipboard]);

  // Context menu, copy, focus, visibility
  useEffect(() => {
    const onContext = (e) => e.preventDefault();
    const onCopy = (e) => e.preventDefault();
    const onVisibility = () => {
      if (document.hidden) {
        setReason("Pantalla oculta");
        setHidden(true);
      } else {
        setHidden(false);
      }
    };
    const onBlur = () => {
      setReason("Pantalla oculta");
      setHidden(true);
    };
    const onFocus = () => setHidden(false);

    document.addEventListener("contextmenu", onContext);
    document.addEventListener("copy", onCopy);
    document.addEventListener("cut", onCopy);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("contextmenu", onContext);
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("cut", onCopy);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // DevTools heuristic
  useEffect(() => {
    const id = setInterval(() => {
      const threshold = 200;
      const wDiff = window.outerWidth - window.innerWidth;
      const hDiff = window.outerHeight - window.innerHeight;
      if (wDiff > threshold || hDiff > threshold) {
        setReason("Inspección detectada");
        setHidden(true);
      }
    }, 1500);
    return () => clearInterval(id);
  }, []);

  const watermarkCells = useMemo(
    () => Array.from({ length: 18 }, (_, i) => `wm-${i}`),
    [],
  );

  return (
    <div className="relative select-none" data-testid="screen-protection-wrapper">
      <div style={{ userSelect: "none", WebkitUserSelect: "none" }}>{children}</div>

      {/* Watermark overlay */}
      <div className="pointer-events-none fixed inset-0 z-[40] overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-6 opacity-[0.07]">
          {watermarkCells.map((key) => (
            <div
              key={key}
              className="flex items-center justify-center text-[11px] tracking-widest text-white"
              style={{ transform: "rotate(-28deg)" }}
            >
              {username.toUpperCase()} · {new Date().toLocaleDateString()}
            </div>
          ))}
        </div>
      </div>

      {/* Black-out screen */}
      {hidden && (
        <div
          data-testid="screen-blackout"
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black text-white"
        >
          <div className="text-center">
            <div className="mb-3 text-xs uppercase tracking-[0.5em] text-white/40">
              {reason || "Vista protegida"}
            </div>
            <div className="text-lg font-light text-white/80">
              Contenido bloqueado temporalmente
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
